import { registerPlugin, WebPlugin } from '@capacitor/core';

import { ALLE_ALTEN_NAMEN, mitNeuenNamen as ausAltenNamen, neuerWert } from './alteNamen';
import { meldeEinstellungsAenderung } from './einstellungenGeaendert';
import { type Verworfen, meldeVerworfene } from './einstellungenLog';
import { DATEN, EINTRAEGE } from './katalog.generiert';

/**
 * Wo eine Einstellung herkommt — und was gewinnt.
 *
 * **Der Auftrag** (Nutzer, 2026-09-13): Alle Einstellungen sollen in den iPad-Einstellungen
 * stehen, damit eine Geräteverwaltung sie setzen kann; Änderungen in der App schreiben dorthin
 * zurück; überschreibt das MDM sie danach wieder, ist das hingenommen.
 *
 * ⚠️ **Das `Settings.bundle` ist nicht der MDM-Kanal.** Was dort steht, setzt der Benutzer in
 * der Einstellungen-App. Ein MDM schiebt Werte über die **Managed App Configuration** — ein
 * schreibgeschütztes Wörterbuch unter `com.apple.configuration.managed` in `NSUserDefaults`.
 * Deshalb sind es hier **zwei** Quellen und nicht eine.
 *
 * ```
 * QR       ein gescannter Einstellungscode   schlägt alles, auch das MDM
 *   ▼
 * MDM      com.apple.configuration.managed   nur lesbar
 *   ▼
 * iPad     Settings.bundle                   hierhin schreibt der App-Dialog zurück
 *   ▼
 * App      die Datenbank der App             Rückfall
 *   ▼
 * Vorgabe  diese Datei
 * ```
 *
 * ⚠️ **Die oberste Stufe ist neu und kehrt die alte Ordnung um** (Entscheidung des Nutzers,
 * 2026-09-13). Bis dahin war das MDM die letzte Wahrheit. Wer einen QR-Code des Servers
 * scannt, setzt sich jetzt bewusst darüber hinweg — das erspart beim Einrichten vieler Geräte
 * den Umweg über die Verwaltung, hebelt aber deren Sperre aus. Die Oberfläche muss deshalb
 * zeigen, dass ein Wert aus einem Code stammt, sonst sucht ein Verwalter den Fehler in seinem
 * Profil.
 *
 * ⚠️ **Fremde Werte werden geprüft, nicht geglaubt.** Ein MDM-Profil schickt, was jemand
 * hineingeschrieben hat. Eine Zeichenkette in einem Schalter oder ein Tippfehler in der
 * Protokollwahl darf die App nicht in einen Zustand bringen, den keine Oberfläche je erzeugen
 * könnte — `alsModus` etwa kennt nur `'classic'` und `'relay'` und setzt für alles andere die
 * Katalogvorgabe ein. Was nicht passt, fällt auf die nächste Quelle zurück.
 */

/**
 * Woher ein Wert kommt. `pruefung`: Relay setzt ihn für die ausgewählte Prüfung;
 * `appPruefung`: in der App geändert, gilt nur für diese Prüfung (beide seit 2026-10-10).
 */
export type Quelle = 'appPruefung' | 'pruefung' | 'qr' | 'mdm' | 'ipad' | 'app' | 'vorgabe';

export type Wert = string | number | boolean;

interface Eintrag<T extends Wert = Wert> {
  vorgabe: T;
  /** Prüft einen Wert aus fremder Hand. `null` heisst: unbrauchbar, nächste Quelle. */
  pruefe: (roh: unknown) => T | null;
  /**
   * Was die Prüfregel genommen hätte, in Worten — `einer von: relay, classic`.
   *
   * ⚠️ **Nur für das Dateilog** ([[einstellungenLog]]). Eine verworfene Zeile ohne diese Angabe
   * sagt dem Verwalter, dass sein Wert falsch war, aber nicht, was richtig gewesen wäre — und
   * genau danach sucht er.
   */
  erwartet: string;
  /** Trägt die Einstellung ein Passwort? Dann geht der Wert **nicht** ins Log. */
  geheim: boolean;
  /**
   * Darf ein gescannter Code oder Relay den Wert ausdrücklich **leeren** (`LEER`)? Nur bei
   * Text — ein leerer Schalter oder eine leere Auswahl hat keine Bedeutung.
   */
  leerbar?: boolean;
}

const text = (vorgabe = '', geheim = false): Eintrag<string> => ({
  vorgabe,
  erwartet: 'Text',
  geheim,
  leerbar: true,
  // ⚠️ Leer zählt **nicht** als Angabe: iOS legt die `DefaultValue` aus der `Root.plist` erst
  // an, wenn jemand die Seite dort einmal geöffnet hat. Ohne diese Regel stünde ein frisches
  // iPad ohne Adresse da, obwohl eine in der Plist steht.
  pruefe: (roh) => (typeof roh === 'string' && roh.trim() !== '' ? roh : null),
});

const schalter = (vorgabe = false): Eintrag<boolean> => ({
  vorgabe,
  erwartet: 'wahr oder falsch',
  geheim: false,
  // Ein Schalter auf „aus" ist dagegen sehr wohl eine Aussage.
  pruefe: (roh) => (typeof roh === 'boolean' ? roh : null),
});

const auswahl = <T extends string>(vorgabe: T, erlaubt: readonly T[]): Eintrag<T> => ({
  vorgabe,
  erwartet: 'einer von: ' + erlaubt.join(', '),
  geheim: false,
  pruefe: (roh) => (typeof roh === 'string' && (erlaubt as readonly string[]).includes(roh) ? (roh as T) : null),
});

/**
 * Eine Zahl aus einer festen Liste.
 *
 * ⚠️ **Nicht `zahl` mit Grenzen.** Die Oberfläche bietet Stufen an; ein MDM, das 137 Sekunden
 * setzt, erzeugte sonst einen Zustand, in dem **keine** Schaltfläche als gewählt erscheint —
 * der Prüfer sähe eine Einstellung ohne Wert und könnte nur raten, was gilt.
 */
const auswahlZahl = (vorgabe: number, erlaubt: readonly number[]): Eintrag<number> => ({
  vorgabe,
  erwartet: 'einer von: ' + erlaubt.join(', '),
  geheim: false,
  pruefe: (roh) => {
    const n = typeof roh === 'number' ? roh : typeof roh === 'string' ? Number(roh) : NaN;
    return erlaubt.includes(n) ? n : null;
  },
});

/**
 * Eine Zahl aus einer Spanne — der Schieber.
 *
 * ⚠️ **Beschneiden statt verwerfen**, als einziger Typ (Entscheidung des Nutzers,
 * 2026-09-17). Bei einer Auswahl ist ein fremder Wert entweder in der Liste oder Unsinn; bei
 * einer Spanne gibt es keinen falschen Wert, nur einen ausserhalb des Zauns. Ein verworfener
 * Wert fiele auf die nächste Quelle zurück und verschluckte die Absicht der Aufsicht still —
 * genau die Stille, die bei Relay zwei Wochen lang einen Farbwert gekostet hat.
 *
 * ⚠️ **Es wird gerundet.** `PSSliderSpecifier` legt einen Float ab: aus einer eingestellten 30
 * wird `30.000001907348633`. Ohne Runden stünde der Regler dauerhaft daneben und jeder
 * Vergleich auf Gleichheit schlüge fehl.
 *
 * ⚠️ **Wahrheitswerte fallen durch.** `typeof true === 'boolean'` läuft in den `NaN`-Zweig;
 * ohne den ausdrücklichen `string`-Zweig machte `Number(true)` daraus eine 1.
 */
const schieber = (vorgabe: number, min: number, max: number): Eintrag<number> => ({
  vorgabe,
  erwartet: 'Zahl von ' + min + ' bis ' + max,
  geheim: false,
  pruefe: (roh) => {
    const n = typeof roh === 'number' ? roh : typeof roh === 'string' ? Number(roh) : Number.NaN;
    if (!Number.isFinite(n)) return null;
    return Math.min(max, Math.max(min, Math.round(n)));
  },
});

/**
 * Alle Einstellungen der App, unter dem Namen, den auch die `Root.plist` führt.
 *
 * ⚠️ **Diese Tabelle ist die Wahrheit**, nicht die `Root.plist`. Wer dort einen Schlüssel
 * ergänzt und hier nicht, bekommt eine Einstellung, die niemand liest.
 */
/**
 * Alle Einstellungen der App — **erzeugt** aus `settings/katalog.json`
 * (`tools/settings-generieren.mjs`; die Texte pflegt seit 2026-10-02 Relays Doku-Seite).
 *
 * ⚠️ **Bis zum 2026-09-17 stand diese Tabelle hier von Hand** und musste mit der `Root.plist`,
 * den drei `Root.strings` und der App-Oberfläche in Deckung gehalten werden — vier Orte für
 * eine Einstellung. Wer einen Schlüssel nur in der Plist ergänzte, bekam eine Einstellung, die
 * niemand liest. Jetzt entsteht alles aus einer Quelle.
 *
 * ⚠️ **Die Prüfregeln bleiben hier.** Sie sind Verhalten, keine Daten: welcher fremde Wert
 * durchgelassen wird, hängt am Typ und an der Werteliste, und beides steht in der Quelle. Ein
 * Generator, der Code für Prüfregeln schriebe, verlagerte Logik in ein Textbaustein-Werkzeug.
 */
export const KATALOG = Object.fromEntries(
  Object.entries(DATEN).map(([name, d]) => {
    const werte = 'werte' in d ? (d.werte as readonly Wert[]) : null;

    if (d.typ === 'schalter') return [name, schalter(d.vorgabe as boolean)];
    if (d.typ === 'schieber') {
      const { min, max } = d as unknown as { min: number; max: number };
      return [name, schieber(d.vorgabe as number, min, max)];
    }
    if (d.typ === 'auswahlZahl') {
      return [name, auswahlZahl(d.vorgabe as number, werte as readonly number[])];
    }
    if (d.typ === 'auswahl') {
      return [name, auswahl(d.vorgabe as string, werte as readonly string[])];
    }
    // `text` und `passwort` unterscheiden sich in der Anzeige — und im Log: leer zählt für
    // beide nicht als Angabe (s. `text`), ein Passwort geht aber nie im Klartext hinaus.
    return [name, text(d.vorgabe as string, d.typ === 'passwort')];
  }),
) as { [K in Schluessel]: Eintrag<WertVon<K>> };

export type Schluessel = keyof typeof DATEN;

/**
 * Welchen Wert eine Einstellung trägt.
 *
 * ⚠️ **Bei einer Auswahl ist es die Vereinigung aller Werte, nicht der Typ der Vorgabe.** Aus
 * `vorgabe: 'relay'` allein würde TypeScript den Literaltyp `'relay'` ableiten — `'classic'`
 * wäre dann ein Fehler, obwohl es in der Werteliste steht. Bei Schalter, Text und Zahl ist es
 * umgekehrt: dort muss der Literaltyp **geweitet** werden, sonst liesse sich ein Schalter mit
 * der Vorgabe `true` nie ausschalten.
 */
type WertVon<K extends Schluessel> = (typeof DATEN)[K] extends { werte: readonly (infer W)[] }
  ? W extends Wert
    ? W
    : never
  : (typeof DATEN)[K]['vorgabe'] extends boolean
    ? boolean
    : (typeof DATEN)[K]['vorgabe'] extends number
      ? number
      : string;

export type Gemischt = {
  [K in Schluessel]: { wert: WertVon<K>; quelle: Quelle };
};

/**
 * Was eine Prüfung für sich setzen darf — Relays Kennzeichen `jePruefung` (Entscheidung des
 * Nutzers, 2026-10-10). Nie ein Passwort, auch wenn das Kennzeichen es behauptete.
 */
export const JE_PRUEFUNG: ReadonlySet<Schluessel> = new Set(
  EINTRAEGE.filter((e) => e.jePruefung && e.typ !== 'passwort').map((e) => e.name as Schluessel),
);

export interface Quellen {
  /** In der App geändert, solange diese Prüfung ausgewählt ist. Nur `JE_PRUEFUNG`. */
  appPruefung?: Partial<Record<Schluessel, unknown>>;
  /** Von Relay für die ausgewählte Prüfung (`Exam.settings`). Nur `JE_PRUEFUNG`. */
  pruefung?: Partial<Record<Schluessel, unknown>>;
  /** Was ein gescannter Einstellungscode gesetzt hat. Liegt in der App-Datenbank. */
  qr: Partial<Record<Schluessel, unknown>>;
  mdm: Partial<Record<Schluessel, unknown>>;
  ipad: Partial<Record<Schluessel, unknown>>;
  app: Partial<Record<Schluessel, unknown>>;
}

/**
 * Eine Quelle vom Gerät in die Namen des Katalogs übersetzen — alte Namen eingeschlossen
 * (`alteNamen.ts`). Nur für die Ablagen auf dem Gerät, nie für das, was vom Draht kommt.
 */
export function mitNeuenNamen<T>(quelle: Record<string, T>): Partial<Record<Schluessel, T>> {
  return ausAltenNamen(quelle, Object.keys(KATALOG) as Schluessel[]);
}

/**
 * Was die App-Datenbank sagt, in den Namen dieses Katalogs.
 *
 * Dort stehen **Zeichenketten** (`ClientSettingValue.value` ist eine Spalte vom Typ Text), auch
 * für Schalter und Zahlen. `'true'`/`'false'` werden deshalb hier zurückübersetzt — `pruefe`
 * eines Schalters nimmt nur echte Wahrheitswerte, und eine Zeichenkette fiele sonst still auf
 * die Vorgabe.
 */
export function ausRelay(settings: Record<string, string>): Partial<Record<Schluessel, unknown>> {
  const ergebnis: Partial<Record<Schluessel, unknown>> = {};
  for (const [name, roh] of Object.entries(mitNeuenNamen(settings)) as [Schluessel, string][]) {
    const eintrag: Eintrag = KATALOG[name];
    ergebnis[name] =
      typeof eintrag.vorgabe === 'boolean'
        ? roh === 'true'
          ? true
          : roh === 'false'
            ? false
            : roh
        : typeof eintrag.vorgabe === 'number'
          ? Number(roh)
          : roh;
  }
  return ergebnis;
}

/**
 * Unter welchem Namen eine Einstellung in die App-Datenbank geht.
 *
 * Seit dem 2026-09-17 ist das **derselbe** Name wie überall sonst — die Funktion bleibt, weil
 * die Aufrufstellen damit sagen, dass sie den Datenbanknamen meinen, und nicht irgendeine
 * Zeichenkette.
 */
export const relayName = (schluessel: Schluessel): string => schluessel;

/**
 * Die Rangfolge, an einer Stelle.
 *
 * `verworfen` ist eine **Sammelliste**: was aus `qr` oder `mdm` durch die Prüfregel fällt, wird
 * hier eingetragen und von `leseEinstellungen` ins Dateilog gemeldet ([[einstellungenLog]]).
 *
 * ⚠️ **Sie ist optional, damit keine Aufrufstelle bricht** — und das Melden geschieht
 * **zentral** in `leseEinstellungen`, nicht an dessen sechs Aufrufstellen. Eine Stelle, die es
 * vergäße, wäre genau der Fehler wieder, gegen den das Log gebaut ist: eine Einstellung, die
 * still verschwindet.
 *
 * ⚠️ **Gemeldet wird auch dann, wenn eine tiefere Quelle einspringt.** Dass am Ende ein
 * brauchbarer Wert dasteht, ist kein Trost für den Verwalter, dessen Profil nicht greift.
 */
/**
 * Ein ausdrücklich geleerter Wert — nur aus der QR-Ablage (`persistence/qrUeberschreibungen.ts`).
 *
 * ⚠️ **Nicht `''`.** Eine leere Zeichenkette heisst in jeder Quelle „keine Angabe" und fällt
 * auf die nächste durch (s. `text`). Seit dem 2026-10-01 kann Relay einen Wert aber auch
 * **leeren** (`''` in `SET_SETTINGS`, Entscheidung des Relay-Nutzers): dann soll leer gelten,
 * auch wenn MDM oder iPad-Seite etwas anderes sagen. Das braucht eine eigene Marke.
 */
export const LEER: unique symbol = Symbol('leer');

export function mische(quellen: Quellen, verworfen?: Verworfen[]): Gemischt {
  const ergebnis = {} as Record<string, { wert: Wert; quelle: Quelle }>;
  for (const [name, eintrag] of Object.entries(KATALOG) as [Schluessel, Eintrag][]) {
    let gesetzt: { wert: Wert; quelle: Quelle } | null = null;
    // Rangfolge, oben gewinnt: App in dieser Prüfung, Prüfung, QR, MDM, iPad, App-Ablage.
    for (const quelle of ['appPruefung', 'pruefung', 'qr', 'mdm', 'ipad', 'app'] as const) {
      if ((quelle === 'appPruefung' || quelle === 'pruefung') && !JE_PRUEFUNG.has(name)) continue;
      const roh = neuerWert(name as Schluessel, quellen[quelle]?.[name]);
      if (roh === LEER) {
        // Ausdrücklich geleert: gilt bei Text, ist bei allem anderen bedeutungslos und fällt durch.
        if (eintrag.leerbar) {
          gesetzt = { wert: '', quelle };
          break;
        }
        continue;
      }
      const geprueft = eintrag.pruefe(roh);
      if (geprueft !== null) {
        gesetzt = { wert: geprueft, quelle };
        break;
      }
      // ⚠️ **Zwei Fälle bleiben still, sonst Lärm statt Signal:** ein Schlüssel, den niemand
      // liefert, und die leere Zeichenkette — iOS legt die `DefaultValue` aus der `Root.plist`
      // erst an, wenn die Seite dort einmal offen war, jedes frische iPad meldete sonst seinen
      // ganzen Katalog. Und nur fremde Hand wird gemeldet: `ipad` und `app` schreibt die App
      // selbst (Wahl des Nutzers, 2026-09-17).
      if (verworfen && (quelle === 'qr' || quelle === 'mdm' || quelle === 'pruefung') && roh !== undefined && roh !== '') {
        verworfen.push({ schluessel: name, quelle, roh, erwartet: eintrag.erwartet, geheim: eintrag.geheim });
      }
    }
    ergebnis[name] = gesetzt ?? { wert: eintrag.vorgabe, quelle: 'vorgabe' };
  }
  return ergebnis as Gemischt;
}

/* ------------------------------------------------------------------ Der native Weg */

export interface SettingsPlugin {
  /** Alle genannten Schlüssel aus `NSUserDefaults` — also aus dem `Settings.bundle`. */
  getAll(options: { keys: string[] }): Promise<{ available: boolean; werte: Record<string, Wert> }>;
  /** Das Wörterbuch der Geräteverwaltung. Leer, wenn keines gesetzt ist. */
  getManaged(): Promise<{ werte: Record<string, Wert> }>;
  /** Schreibt einen Wert zurück in die iPad-Einstellungen. */
  set(options: { key: string; value: Wert | null }): Promise<void>;
}

/**
 * Im Browser gibt es keine iPad-Einstellungen. `available: false` heisst genau das — dann
 * bleiben die Felder in der App die Quelle (derselbe Weg wie bei `getManagedSettings`).
 */
class SettingsWeb extends WebPlugin implements SettingsPlugin {
  async getAll(): Promise<{ available: boolean; werte: Record<string, Wert> }> {
    return { available: false, werte: {} };
  }
  async getManaged(): Promise<{ werte: Record<string, Wert> }> {
    return { werte: {} };
  }
  async set(): Promise<void> {
    // Bewusst still: im Browser ist das Zurückschreiben kein Fehler, sondern gegenstandslos.
  }
}

export const Settings = registerPlugin<SettingsPlugin>('Settings', { web: () => new SettingsWeb() });

/** Liest alle drei Quellen und mischt sie. `app` kommt von aussen — `platform/` kennt den Store nicht. */
export async function leseEinstellungen(
  app: Partial<Record<Schluessel, unknown>> = {},
  /**
   * Was ein gescannter Einstellungscode gesetzt hat. Kommt aus der App-Datenbank
   * (`persistence/qrUeberschreibungen.ts`) und steht über allem — s. die Rangfolge oben.
   */
  qr: Partial<Record<Schluessel, unknown>> = {},
  /** Die zwei Stufen der ausgewählten Prüfung (`persistence/pruefungsEinstellungen.ts`). */
  jePruefung: Pick<Quellen, 'pruefung' | 'appPruefung'> = {},
): Promise<Gemischt> {
  // Auch nach den alten Namen fragen: ein iPad von vor der Umbenennung trägt nur sie.
  const namen = [...Object.keys(KATALOG), ...ALLE_ALTEN_NAMEN];
  const [ipad, mdm] = await Promise.all([
    Settings.getAll({ keys: namen }).catch(() => ({ available: false, werte: {} })),
    Settings.getManaged().catch(() => ({ werte: {} })),
  ]);
  const verworfen: Verworfen[] = [];
  const gemischt = mische(
    { ...jePruefung, qr, mdm: mitNeuenNamen(mdm.werte), ipad: mitNeuenNamen(ipad.werte), app },
    verworfen,
  );
  // ⚠️ **Ohne `await`**: das Lesen der Einstellungen steht im Startweg der App und darf nicht
  // auf ein Dateischreiben warten. `meldeVerworfene` wirft nie.
  void meldeVerworfene(verworfen);
  return gemischt;
}

/**
 * Schreibt eine Einstellung in die iPad-Einstellungen zurück.
 *
 * ⚠️ **Gegen das MDM hilft das nicht.** Steht der Schlüssel dort, gewinnt er beim nächsten
 * Lesen wieder — die Oberfläche muss das zeigen, statt den Prüfer raten zu lassen, warum sein
 * Wert zurückspringt.
 */
export async function schreibeEinstellung(schluessel: Schluessel, wert: Wert | null): Promise<void> {
  await Settings.set({ key: schluessel, value: wert });
  meldeEinstellungsAenderung();
}
